import { type JSX } from 'react/jsx-runtime';
import { createRender, type HydrationResult } from './types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SidenavBanner } from './SidenavBanner';
import { BANNER_CONTENT } from './content';
import { UpgradeModal } from './UpgradeModal';
import { StorageClient } from '@atlaskit/frontend-utilities';

const storageClient = new StorageClient('jira-twcg-810-trial-ad-modal', {
	storageEngine: 'localStorage',
});
const SEEN_KEY = 'seen';

const getSeenValue = (): boolean => storageClient.getItem(SEEN_KEY) === true;
const setSeenValue = (): void => storageClient.setItemWithExpiry(SEEN_KEY, true);

export const render: (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = createRender(
	({
		edition,
		entitlementId,
		recommendedFeature,
		recommendedFeatureHref,
		trialLength,
		headlineCount,
		placeOrderLiteHref,
		shouldForceOpenModal,
		onDismissed,
	}) => {
		const [isModalOpen, setIsModalOpen] = useState(() => shouldForceOpenModal && !getSeenValue());

		const analyticsAttributes = useMemo(
			() => ({
				edition,
				recommendedFeature,
				trialLength,
				entitlementId,
			}),
			[edition, recommendedFeature, trialLength, entitlementId],
		);

		useEffect(() => {
			if (isModalOpen && !getSeenValue()) {
				setSeenValue();
			}
		}, [isModalOpen]);

		const handleBannerClick = useCallback(() => {
			setIsModalOpen(true);
		}, []);

		const handleModalClose = useCallback(() => {
			setSeenValue();
			setIsModalOpen(false);
		}, []);

		const handleDismissBanner = useCallback(() => {
			setSeenValue();
			onDismissed();
		}, [onDismissed]);

		// Trial completed: close modal AND dismiss the banner so it doesn't reappear.
		const handleModalComplete = useCallback(() => {
			setIsModalOpen(false);
			onDismissed();

			if (recommendedFeature === 'rovo') {
				try {
					const currentUrl = new URL(globalThis.window.location.href);
					currentUrl.searchParams.set('rovoChatTriggerOpen', 'true');
					globalThis.window.location.href = currentUrl.toString();
					return;
				} catch {
					// Fall back to the recommended feature href below.
				}
			}

			globalThis.window.location.href = recommendedFeatureHref;
		}, [onDismissed, recommendedFeature, recommendedFeatureHref]);

		const { Icon, TitleMessage, CtaMessage } = BANNER_CONTENT[recommendedFeature];

		return (
			<>
				<SidenavBanner
					onClick={handleBannerClick}
					onDismiss={handleDismissBanner}
					icon={<Icon />}
					title={<TitleMessage count={headlineCount} />}
					cta={<CtaMessage />}
					bannerName="jiraTwcg810UpgradeFreeCustomersBanner"
					analyticsAttributes={analyticsAttributes}
				/>
				<UpgradeModal
					isOpen={isModalOpen}
					onClose={handleModalClose}
					onComplete={handleModalComplete}
					recommendedFeature={recommendedFeature}
					edition={edition}
					trialLength={trialLength}
					headlineCount={headlineCount}
					placeOrderLiteHref={placeOrderLiteHref}
				/>
			</>
		);
	},
);

export default render;
